import { ControlInlineEdit } from "aic";
import { omit } from "lodash";
import { useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { DropdownItem, DropdownPortal } from "../../controls/DropdownPortal";
import { getFromDataPath } from "../../utility";
import { classMerge } from "../../utility/classMerge";
import { WhereClause } from "../ars_ldm";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { isWhereClause } from "../typeGuards";
import { CUSTOM_DATASET_VARIABLE_WARNING } from "../utility/customDatasetVariable";
import { formatWhereClauseAsCode } from "../utility/formatWhereClauseAsCode";
import {
  ParseWhereClauseFromCodeReturn,
  parseWhereClauseFromCode,
} from "../utility/parseWhereClauseFromCode";
import styles from "./WhereClauseConditionItemControl.module.css";

/**
 * Extracts the last contiguous variable-name token from the end of a string.
 * A token is a sequence of alphanumeric characters, underscores, and dots.
 *
 * @param value - The input string to extract the last token from
 * @returns The last token, or an empty string if none found
 */
const getLastToken = (value: string): string => /[A-Za-z0-9_.]+$/.exec(value)?.[0] ?? "";

/**
 * Replaces the last variable-name token in a string with a replacement and appends a space.
 * Uses a `*`-quantifier so the regex always matches, handling both cases (token present
 * or absent) without branching: when no token exists an empty string is replaced, which
 * has the same effect as appending the replacement.
 *
 * @param inputValue - The current input string
 * @param replacement - The string to replace the last token with
 * @returns The updated string with the token replaced and a trailing space appended
 */
const replaceLastToken = (inputValue: string, replacement: string): string =>
  inputValue.replace(/[A-Za-z0-9_.]*$/, `${replacement} `);

/**
 * Props for the WhereClauseInput component.
 */
export interface WhereClauseInputProps {
  /** RFC 6901 JSON Pointer path to the entity in ArsContext state */
  dataPath: string;
  /** Disabled state */
  disabled?: boolean;
  /** Optional callback function to handle changes to the where clause instead of dispatching to ArsContext */
  onChange?: (newValue: WhereClause | null) => void;
  /** Where clause to display instead of reading it from ArsContext state at `dataPath` */
  value?: WhereClause | null;
}

/**
 * An input component for editing where clause expressions that provides variable
 * autocompletion via a dropdown portal.
 *
 * When 3 or more characters of a variable-like token are typed, a dropdown appears
 * showing matching variables from the available list. The user can navigate with
 * arrow keys and select a variable with Enter, which inserts it into the input and
 * allows typing to continue. The dropdown can be dismissed with Escape.
 *
 * Pressing Enter when no dropdown is visible blurs the input to trigger save.
 * ControlInlineEdit commits or cancels edits when the variable dropdown is closed.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the entity in ArsContext state.
 * @param props.disabled - Whether the input is disabled.
 * @param props.onChange - Optional callback function to handle changes to the where clause instead of dispatching to ArsContext
 * @param props.value - Optional where clause to display instead of reading it from ArsContext state
 * @returns A React functional component rendering the where clause input with variable dropdown.
 */
export const WhereClauseInput = ({
  dataPath,
  disabled = false,
  onChange,
  value,
}: WhereClauseInputProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const [isEditing, setIsEditing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentValue, setCurrentValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  /** The token value for which the dropdown was explicitly closed by the user */
  const [closedForToken, setClosedForToken] = useState<string | null>(null);

  const whereClause = value ?? getFromDataPath(dataPath, state, isWhereClause);
  const compactCode = whereClause ? formatWhereClauseAsCode(whereClause, state.variables) : "";
  const availableVariables = state.variables.map((variable) => variable.value);
  const valueToValidate = isEditing ? currentValue : compactCode;
  const parsedWhereClauseResult: ParseWhereClauseFromCodeReturn =
    valueToValidate.length > 0
      ? parseWhereClauseFromCode(
          valueToValidate,
          availableVariables,
          Object.keys(state.dataSubsets),
        )
      : false;
  const isValid = parsedWhereClauseResult !== false;
  const isWarning = parsedWhereClauseResult && parsedWhereClauseResult.acceptedUnlistedVariable;
  const isInvalid = isEditing && currentValue.length > 0 && !isValid;

  // Get the last variable-like token being typed (e.g. "ADSL.AG" from "x = 1 AND ADSL.AG")
  const lastToken = getLastToken(currentValue);

  // Filter available variables matching the last token (requires 3+ chars)
  const filteredVariables =
    lastToken.length >= 3
      ? state.variables.filter((v) => v.value.toLowerCase().includes(lastToken.toLowerCase()))
      : [];

  // Show dropdown when matches exist and the user has not explicitly closed it for this token
  const isDropdownOpen = isEditing && filteredVariables.length > 0 && closedForToken !== lastToken;

  // Build dropdown items from the filtered variable list
  const inputId = `${dataPath.replace(/\//g, "-")}-edit-input`;
  const dropdownItems: DropdownItem[] = filteredVariables.map((v, i) => ({
    id: `${inputId}-var-${i}`,
    text: v.value,
  }));

  /**
   * Saves edits back into ARS state when parsing succeeds.
   *
   * @param value - Edited where clause code
   */
  const saveChanges = (value: string): void => {
    // handle onChange first
    if (onChange) {
      if (value.trim() === "") {
        onChange(null);
      } else {
        const parsed = parseWhereClauseFromCode(
          value,
          availableVariables,
          Object.keys(state.dataSubsets),
        );
        if (parsed) {
          onChange(parsed.whereClause);
        }
      }
      return;
    }

    // handle dispatch changes - include non-WhereClause properties to avoid overwriting them
    const parsed = parseWhereClauseFromCode(
      value,
      availableVariables,
      Object.keys(state.dataSubsets),
    );
    // Replace valid or blank clauses
    if (parsed || value.trim() === "") {
      const newValue = {
        ...omit(whereClause, ["compoundExpression", "condition"]),
        ...(parsed
          ? parsed.whereClause
          : {
              level: 0,
              order: 0,
              condition: { dataset: "", variable: "" },
            }),
      };
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: newValue,
      });
    }
  };

  /**
   * Inserts a selected variable into the input by replacing the last token,
   * appending a trailing space, and resetting dropdown state.
   * Reads the current DOM value directly to avoid stale debounced state.
   *
   * @param variable - The variable string to insert into the input
   */
  const insertVariable = (variable: string): void => {
    const inputEl = wrapperRef.current?.querySelector<HTMLInputElement>("input");
    const inputValue =
      inputEl?.value ??
      // istanbul ignore next -- Unreachable: wrapperRef always contains an input when this function is called
      currentValue;
    const newValue = replaceLastToken(inputValue, variable);
    setCurrentValue(newValue);
    setSelectedIndex(-1);
    setClosedForToken(null);
  };

  /**
   * Handles keyboard events in the capture phase to intercept navigation keys.
   *
   * When the dropdown is open:
   * - ArrowDown / ArrowUp: navigate the highlighted item
   * - Enter: insert the highlighted variable (or close dropdown if none highlighted)
   * - Escape: close the dropdown and reset selection
   *
   * When the dropdown is closed, keyboard events reach ControlInlineEdit unchanged.
   *
   * @param e - The keyboard event from the capture phase
   */
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (isDropdownOpen) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prev) => Math.min(prev + 1, dropdownItems.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          if (selectedIndex >= 0 && selectedIndex < dropdownItems.length) {
            insertVariable(dropdownItems[selectedIndex].text);
          } else {
            setClosedForToken(lastToken);
          }
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          setClosedForToken(lastToken);
          setSelectedIndex(-1);
          break;
      }
    }
  };

  return (
    <div>
      <div
        ref={wrapperRef}
        onKeyDownCapture={handleKeyDown}
      >
        <ControlInlineEdit
          id={inputId}
          aria-label="Enter where clause"
          editLabel="Edit where clause"
          disabled={disabled}
          value={compactCode}
          draftValue={currentValue}
          setValue={saveChanges}
          onDraftChange={setCurrentValue}
          onEditChange={(editing) => {
            setIsEditing(editing);
            setSelectedIndex(-1);
            setClosedForToken(null);
          }}
          editOnDoubleClick
          preventInvalidExit={false}
          spellCheck={false}
          isInvalid={isInvalid}
          className={classMerge([
            isWarning
              ? "is-warning"
              : isValid && currentValue.length > 0
                ? "is-valid"
                : isInvalid
                  ? "is-invalid"
                  : "",
          ])}
          whiteSpaceFix={true}
          ReadComponent={({ value }) => (
            <div
              className="form-control form-control-sm"
              style={{ border: "none" }}
            >
              <code className={styles.codeBlock}>
                {value === "" ? <span className="text-muted">No where clause defined</span> : value}
              </code>
            </div>
          )}
        />
        <DropdownPortal
          isOpen={isDropdownOpen && !disabled}
          items={dropdownItems}
          onSelect={(item) => insertVariable(item.text)}
          onClose={() => {
            setClosedForToken(lastToken);
            setSelectedIndex(-1);
          }}
          anchorRef={wrapperRef}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={setSelectedIndex}
        />
      </div>
      <Form.Text
        style={{
          height: isEditing ? "1.5em" : 0,
          minHeight: isEditing ? "1.5em" : 0,
          transition: "height 0.2s ease, min-height 0.2s ease",
        }}
        className={classMerge([
          isWarning
            ? "warning-feedback"
            : isValid && currentValue.length > 0
              ? "valid-feedback"
              : isInvalid
                ? "invalid-feedback"
                : "text-muted",
          styles.validationText,
        ])}
      >
        {isWarning
          ? CUSTOM_DATASET_VARIABLE_WARNING
          : isValid && currentValue.length > 0
            ? "Valid where clause"
            : isInvalid
              ? "Invalid where clause format"
              : ""}
      </Form.Text>
    </div>
  );
};

WhereClauseInput.displayName = "WhereClauseInput";
